/* eslint-disable consistent-return */
import React, { useState, useEffect } from 'react';
import { node, string, func, bool } from 'prop-types';
import classNames from 'classnames';
import OutsideClickHandler from 'react-outside-click-handler';

import css from './Dropdown.module.css';

const Dropdown = ({
  className,
  triggerClassName,
  triggerActiveClassName,
  triggerOpenClassName,
  contentClassName,
  trigger,
  children,
  isOpen: controlledIsOpen,
  onToggle,
  closeOnOutsideClick = true,
  closeOnEscape = true,
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);

  // Use controlled state if provided, otherwise use internal state
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const setIsOpen = onToggle || setInternalIsOpen;

  // Handle escape key to close dropdown
  useEffect(() => {
    if (!closeOnEscape) return;

    const handleEscKey = event => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscKey);

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, closeOnEscape, setIsOpen]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleOutsideClick = () => {
    if (closeOnOutsideClick && isOpen) {
      setIsOpen(false);
    }
  };

  const wrapperClasses = classNames(css.dropdownWrapper, className);

  const triggerClasses = classNames(css.dropdownTrigger, triggerClassName, {
    [triggerActiveClassName || css.dropdownTriggerActive]: isOpen,
    [triggerOpenClassName || css.dropdownTriggerOpen]: isOpen,
  });

  const contentClasses = classNames(css.dropdownContent, contentClassName);

  const triggerElement = React.cloneElement(trigger, {
    className: classNames(trigger.props.className, triggerClasses),
    onClick: handleToggle,
    onKeyDown: e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleToggle();
      }
      // Call original onKeyDown if it exists
      if (trigger.props.onKeyDown) {
        trigger.props.onKeyDown(e);
      }
    },
    role: trigger.props.role || 'button',
    tabIndex: trigger.props.tabIndex !== undefined ? trigger.props.tabIndex : 0,
    'aria-expanded': isOpen,
    'aria-haspopup': true,
  });

  return (
    <OutsideClickHandler onOutsideClick={handleOutsideClick}>
      <div className={wrapperClasses}>
        {triggerElement}
        {isOpen && <div className={contentClasses}>{children}</div>}
      </div>
    </OutsideClickHandler>
  );
};

Dropdown.defaultProps = {
  className: null,
  triggerClassName: null,
  triggerActiveClassName: null,
  triggerOpenClassName: null,
  contentClassName: null,
  isOpen: undefined,
  onToggle: null,
  closeOnOutsideClick: true,
  closeOnEscape: true,
};

Dropdown.propTypes = {
  className: string,
  triggerClassName: string,
  triggerActiveClassName: string,
  triggerOpenClassName: string,
  contentClassName: string,
  trigger: node.isRequired,
  children: node.isRequired,
  isOpen: bool,
  onToggle: func,
  closeOnOutsideClick: bool,
  closeOnEscape: bool,
};

export default Dropdown;
